// Per-job run days on a login. A job with its own `days` runs only on those days; a job
// without them runs every day the login runs. Older registries send neither `days` nor
// `today.jobs`, and then everything here renders exactly as before.

const ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const ownDays = (job) => (Array.isArray(job?.days) && job.days.length ? job.days : null);

// ['mon'] -> 'Mon'; ['mon','wed'] -> 'Mon, Wed'; mon-fri -> 'Weekdays'; all 7 -> 'Daily'.
export const fmtDays = (days) => {
    const d = ORDER.filter((x) => (days || []).includes(x));
    if (d.length === 7) return 'Daily';
    if (d.join(',') === 'mon,tue,wed,thu,fri') return 'Weekdays';
    return d.map((x) => x[0].toUpperCase() + x.slice(1)).join(', ');
};

// Data source cell: one entry per source in job order, "(Mon)" only when every job of that
// source has its own days. No suffix anywhere -> the old ", " join, byte for byte.
export const sourcesText = (row) => {
    const jobs = row.jobs || [];
    const bySource = new Map();
    jobs.forEach((j) => {
        const s = j.data_source || row.vendor;
        bySource.set(s, [...(bySource.get(s) || []), j]);
    });
    const parts = [...bySource].map(([s, js]) => {
        if (!js.every(ownDays)) return { s, text: s };
        return { s, text: `${s} (${fmtDays([...new Set(js.flatMap(ownDays))])})`, suffixed: true };
    });
    return parts.map((p) => p.text).join(parts.some((p) => p.suffixed) ? ' · ' : ', ');
};

// 'today 12:44 PM' on the row's day, else 'Oct 13, 9:10 AM', in the login's timezone
// and the browser's locale (as the Last run column).
export const fmtWhen = (iso, tz, day, today) => {
    try {
        const opts = { timeZone: tz, hour: 'numeric', minute: '2-digit' };
        return day && day === today
            ? `today ${new Date(iso).toLocaleTimeString([], opts)}`
            : new Date(iso).toLocaleString([], { ...opts, month: 'short', day: 'numeric' });
    } catch {
        return iso;
    }
};

// 'Oct 14' from '2026-10-14' (a calendar day, so no timezone shift).
const fmtDay = (day) => {
    try {
        return new Date(`${day}T12:00:00Z`).toLocaleDateString([], { timeZone: 'UTC', month: 'short', day: 'numeric' });
    } catch {
        return day;
    }
};

// ' · last: completed today 12:44 PM' per job; '' when the API predates jobs_last. With none
// found: 'none yet' only when run records cover the whole lookback, else 'none since Oct 14'.
const lastText = (row, jt) => {
    if (!row.jobs_last) return '';
    const l = row.jobs_last[jt];
    if (l) return ` · last: ${l.status} ${fmtWhen(l.at, row.timezone, l.day, row.today?.day)}`;
    const since = row.records_since;
    return since && (!row.jobs_last_from || since > row.jobs_last_from)
        ? ` · last: none since ${fmtDay(since)}` : ' · last: none yet';
};

// Hover lines for the Data source cell, or null when there's nothing a hover would add.
// `label` names a job (the console passes deskModel's jobLabel); default: its job_name.
const rawLabel = (j) => j.job_name || j.job_type;
export const jobDayLines = (row, loginDays, label = rawLabel) => {
    const jobs = row.jobs || [];
    if (!jobs.some(ownDays) && jobs.length < 2 && !row.jobs_last) return null;
    return jobs.map((j) => `${label(j)} — ${fmtDays(ownDays(j) || loginDays || [])}`
        + (j.catch_up === 'week' ? ', catch-up later in the week' : '') + lastText(row, j.job_type));
};

// Last run cell suffix: ' (on request)' as before; with the newer API also what it ran:
// ' (on request, test: HMH)' for a scratch run, ' (Amira)' when it ran only some data sources.
export const lastRunKind = (row) => {
    const last = row.last_run;
    if (!last) return '';
    const parts = last.adhoc ? ['on request'] : [];
    if (row.jobs_last && Array.isArray(last.job_types)) {
        const jobs = row.jobs || [];
        const srcOf = (jt) => {
            const j = jobs.find((x) => x.job_type === jt);
            return j?.data_source || j?.job_name || jt;
        };
        const ran = [...new Set(last.job_types.map(srcOf))];
        const all = new Set(jobs.map((j) => srcOf(j.job_type)));
        const subset = ran.length < all.size || ran.some((s) => !all.has(s));
        if (last.scratch) parts.push(`test${ran.length ? `: ${ran.join(', ')}` : ''}`);
        else if (subset && ran.length) parts.push(ran.join(', '));
    } else if (row.jobs_last && last.scratch) {
        parts.push('test');
    }
    return parts.length ? ` (${parts.join(', ')})` : '';
};

// Today cell: { due, total, names } when only some of the login's jobs are due today, else null.
export const dueToday = (row, label = rawLabel) => {
    const due = row.today?.jobs;
    const total = (row.jobs || []).length;
    if (!Array.isArray(due) || !due.length || due.length >= total) return null;
    const nameOf = (jt) => { const j = row.jobs.find((x) => x.job_type === jt); return j ? label(j) : jt; };
    return { due: due.length, total, names: due.map(nameOf) };
};
